// page_view v2 (MOB-470): один на экран, шлётся из router.afterEach рядом с
// автоматическим page_view GA4 (send_page_view: true не трогаем).
import type { NavigationFailure, RouteLocationNormalized } from 'vue-router'
import { store } from '@web/store'
import PAGE_NAME from '@web/consts/PAGE_NAME'
import type { PageType } from '@web/services/Analytics/registry'
import { setCurrentPage } from '@web/services/Analytics/v2/currentPage'
import { track } from '@web/services/Analytics/v2/track'
import Logger from '@web/common/Logger'

const PAGE_TYPE_BY_ROUTE: Record<string, PageType> = {
  [PAGE_NAME.HOME]: 'home',
  [PAGE_NAME.HOME_CATALOG]: 'catalog',
  [PAGE_NAME.CATALOG]: 'catalog',
  [PAGE_NAME.NEW_COURSE]: 'course_page',
  [PAGE_NAME.CALENDAR]: 'calendar',
  [PAGE_NAME.DAY]: 'course_day',
  [PAGE_NAME.SUBSCRIPTIONS]: 'subscriptions',
  [PAGE_NAME.SUBSCRIPTION]: 'subscriptions',
  [PAGE_NAME.SEARCH]: 'search',
  [PAGE_NAME.SUPPORT]: 'support',
  [PAGE_NAME.SUPPORT_CHAT]: 'support',
  [PAGE_NAME.SUPPORT_CATEGORY]: 'support',
  [PAGE_NAME.SUPPORT_CATEGORY_STREAM]: 'support',
  [PAGE_NAME.CLUB_CHOOSE]: 'club_feed',
  [PAGE_NAME.CLUB_CATEGORY]: 'club_feed',
  [PAGE_NAME.CLUB_TAG]: 'club_feed',
  [PAGE_NAME.CLUB_ARTICLE]: 'article',
  [PAGE_NAME.CLUB_ARTICLE_PREVIEW]: 'article'
}

// Какой параметр роута — сущность экрана (без риска путаницы пространств id).
const OBJECT_PARAM: Partial<Record<PageType, string>> = {
  subscriptions: 'id',
  article: 'id'
}

// Экраны обучения адресуются курсом-контентом, а в роуте у них поток.
const STREAM_PAGES: ReadonlyArray<PageType> = ['calendar', 'course_day']

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const

let isFirstPageView = true

// Для тестов.
export function resetFirstPageView (): void {
  isFirstPageView = true
}

export function courseIdOfStream (streamId: string): string | undefined {
  const streams: Array<{ id: number, courseId: number }> = store.getters['stream/streams'] || []
  const stream = streams.find(item => item.id === Number(streamId))
  return stream ? String(stream.courseId) : undefined
}

export function pageTypeOf (route: RouteLocationNormalized): PageType {
  return PAGE_TYPE_BY_ROUTE[String(route.name)] ?? 'other'
}

function first (value: unknown): string | undefined {
  const item = Array.isArray(value) ? value[0] : value
  return item ? String(item) : undefined
}

function utmOf (route: RouteLocationNormalized): Partial<Record<typeof UTM_KEYS[number], string>> {
  const utm: Partial<Record<typeof UTM_KEYS[number], string>> = {}
  UTM_KEYS.forEach(key => {
    const value = first(route.query[key])
    if (value) utm[key] = value
  })
  return utm
}

export function handleRouteChange (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  failure?: NavigationFailure | void,
  courseIdOf: (streamId: string) => string | undefined = courseIdOfStream
): void {
  if (failure) return
  // Исключение в afterEach сорвало бы остальные хуки и router.push — не бросаем.
  try {
    trackPageView(to, from, courseIdOf)
  } catch (e) {
    Logger.error('[Analytics v2] page_view не отправлен', e)
  }
}

function trackPageView (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  courseIdOf: (streamId: string) => string | undefined
): void {
  const pageType = pageTypeOf(to)
  setCurrentPage({ page_type: pageType, page_path: to.path })

  // Сменились только query или hash — это тот же экран.
  if (from.matched.length > 0 && from.path === to.path) return

  const objectKey = OBJECT_PARAM[pageType]
  const objectId = objectKey ? first(to.params[objectKey]) : undefined
  const streamId = STREAM_PAGES.includes(pageType) ? first(to.params.streamId) : undefined
  const courseId = pageType === 'course_page' ? first(to.params.courseId) : streamId ? courseIdOf(streamId) : undefined
  const dayNumber = pageType === 'course_day' ? Number(first(to.params.dayNumber)) : NaN
  const utm = isFirstPageView ? utmOf(to) : {}
  isFirstPageView = false

  track('page_view', {
    ...(objectId ? { object_id: objectId } : {}),
    ...(courseId ? { course_id: courseId } : {}),
    ...(Number.isFinite(dayNumber) ? { day_number: dayNumber } : {}),
    ...utm
  })
}
